import PropTypes from 'prop-types'
import sortBy from 'lodash/sortBy'
import styled from 'styled-components'
import { useSelector } from 'react-redux'
import { useEffect, useState, useMemo } from 'react'

import * as partnersSelector from 'selectors/partners'
import * as sessionSelector from 'selectors/session'
import * as sessionUserSelector from 'selectors/sessionUser'

import { useUpdatePartnerViewMutation } from 'api/sessionUser'

import { capitalizeFirstLetter, truncate } from 'utils/strings'

import NavBarMenu from './NavBarMenu/Index'
import { NavBarMenuItemStyles } from './NavBarMenu/NavBarMenuItem'

import { MEGA_MENU_LINE_LENGTH } from 'constants/constants'

const ADMIN_OR_POWER_USER_ID = -1

const StyledNavPartnersTitle = styled.div`
  display: flex;
  align-items: center;
  height: 48px;
`

const StyledNavBarPartnerMenuItem = styled.div`
  ${NavBarMenuItemStyles}

  &:focus {
    outline: none;
  }
`

const PartnerLogo = styled.img`
  width: auto;
  height: 18px;
  padding-right: 2px;
`

const NavPartnersTitle = ({ selectedPartnerName, selectedPartnerIcon, adminOrPowerUserItemName }) => {
  let name = ''
  if (!selectedPartnerIcon) {
    name = selectedPartnerName || adminOrPowerUserItemName
  }

  return (
    <StyledNavPartnersTitle>
      {selectedPartnerIcon && <PartnerLogo src={selectedPartnerIcon} />}
      {name && <div>{truncate(name, MEGA_MENU_LINE_LENGTH)}</div>}
    </StyledNavPartnersTitle>
  )
}

NavPartnersTitle.propTypes = {
  selectedPartnerName: PropTypes.string,
  selectedPartnerIcon: PropTypes.string,
  adminOrPowerUserItemName: PropTypes.string,
}

const NavBarPartnerMenuItem = ({ partner, selectPartner, selected, innerRef }) => {
  const className = selected ? 'active' : null

  return (
    <StyledNavBarPartnerMenuItem
      className={className}
      onClick={() => selectPartner(partner.id)}
      onKeyPress={(e) => e.key === 'Enter' && selectPartner(partner.id)}
      onMouseEnter={(e) => e.currentTarget.focus()}
      ref={innerRef}
      tabIndex={0}
    >
      {capitalizeFirstLetter(truncate(partner.name, MEGA_MENU_LINE_LENGTH))}
    </StyledNavBarPartnerMenuItem>
  )
}

NavBarPartnerMenuItem.propTypes = {
  partner: PropTypes.object.isRequired,
  selectPartner: PropTypes.func.isRequired,
  selected: PropTypes.bool,
  innerRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any }),
  ]),
}

const NavPartners = () => {
  const [selectedPartnerId, setSelectedPartnerId] = useState()

  const userIsAdmin = useSelector(sessionUserSelector.isAdmin)
  const publicPartnersList = useSelector(sessionSelector.partners)
  const adminPartnersList = useSelector(partnersSelector.activePartners)
  const selectedPartnerIdFromUser = useSelector(sessionUserSelector.partnerId)
  const userIsPowerUser = useSelector(sessionUserSelector.isPowerUser)
  const isPowerUserImpersonatedRole = useSelector(sessionUserSelector.isPowerUserImpersonatedRole)

  const adminOrPowerUserItemName = (userIsPowerUser || isPowerUserImpersonatedRole) ? 'Power User' : 'Admin'

  const [updatePartnerView, { isLoading, isUninitialized }] = useUpdatePartnerViewMutation()

  // useEffect triggers on mount with isLoading === false so we additionally check isUninitialized
  useEffect(() => {
    if (isLoading === false && !isUninitialized) {
      window.location.href = '/'
    }
  }, [isLoading, isUninitialized])

  useEffect(() => {
    setSelectedPartnerId(selectedPartnerIdFromUser)
  }, [selectedPartnerIdFromUser])

  const selectPartner = (partnerViewId) => {
    const partnerId = partnerViewId === ADMIN_OR_POWER_USER_ID ? null : partnerViewId
    setSelectedPartnerId(partnerId) // optimistic update for faster UX
    updatePartnerView(partnerId)
  }
  const partnersList = userIsAdmin ? adminPartnersList : publicPartnersList

  const sortedPartnersList = useMemo(() => {
    const adminOrPowerUserItem = { id: ADMIN_OR_POWER_USER_ID, name: adminOrPowerUserItemName, position: -1 }
    return sortBy([adminOrPowerUserItem, ...partnersList], ['position'])
  }, [partnersList, adminOrPowerUserItemName])


  if (!partnersList.length) {
    return null
  }

  const selectedPartnerOrAdminId = selectedPartnerId ? selectedPartnerId + '' : ADMIN_OR_POWER_USER_ID
  const selectedPartner = sortedPartnersList.find(p => p.id === selectedPartnerOrAdminId)
  const selectedPartnerName = selectedPartner?.name
  const selectedPartnerIcon = selectedPartner?.icon
  const selectedPartnerNameCapitalized = capitalizeFirstLetter(selectedPartnerName)

  return (
    <NavBarMenu
      noUnderline
      title={
        <NavPartnersTitle
          selectedPartnerIcon={selectedPartnerIcon}
          selectedPartnerName={selectedPartnerNameCapitalized}
          adminOrPowerUserItemName={adminOrPowerUserItemName}
        />
      }
    >
      {sortedPartnersList.map((partner, index) => {
        const selected = partner.id === selectedPartnerOrAdminId

        return (
          <NavBarPartnerMenuItem
            key={partner.id}
            partner={partner}
            selectPartner={selectPartner}
            selected={selected}
          />
        )
      })}
    </NavBarMenu>
  )
}

export default NavPartners
